import React, { useState } from 'react';

const ATSChecker = () => {
  const [file, setFile] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const startScan = () => {
    setIsScanning(true);
    setTimeout(() => {
      setIsScanning(false);
      setResult({
        score: 78,
        improvements: ['Missing metrics in experience', 'Header formatting inconsistencies'],
        link: 'https://www.senvra.ai/#/ats-score?data=ey...'
      });
    }, 2000);
  };

  const reset = () => {
    setFile(null);
    setResult(null);
  };

  return (
    <main className="pt-[140px] pb-20 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-5 text-center">
        {/* Header Section */}
        <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white mb-4">
          Check Your <span className="text-primary">ATS Score</span>
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-lg mb-12 max-w-2xl mx-auto font-medium">
          Most resumes are rejected by robots before a human ever sees them. Upload your resume to see how well it parses and what score it gets.
        </p>

        {/* Main Card Container */}
        <div className="max-w-4xl mx-auto bg-white dark:bg-slate-900 rounded-[40px] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100 dark:border-slate-800 p-1 relative overflow-hidden mb-16">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sky-400 via-purple-500 to-rose-400"></div>
          
          <div className="p-8 md:p-12">
            {/* File Display Box - Shown when file is selected OR when result is displayed */}
            {file && (
              <div className="w-full max-w-md mx-auto bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/50 rounded-2xl p-6 flex items-center justify-between mb-8 text-left animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center gap-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                  <div>
                    <p className="text-sm font-black text-slate-700 dark:text-slate-200 truncate max-w-[200px]">{file.name}</p>
                    <p className="text-[11px] text-blue-500 font-bold uppercase tracking-wider">Ready to scan</p>
                  </div>
                </div>
                <button onClick={reset} className="text-slate-400 hover:text-red-500 transition-colors p-1">✕</button>
              </div>
            )}

            {!result ? (
              /* UPLOAD STATE - image_badf94.png */
              <div className="animate-in fade-in duration-500">
                {!file ? (
                  <label className="cursor-pointer flex flex-col items-center py-10">
                    <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-slate-400" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                    </div>
                    <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Upload your Resume</h3>
                    <p className="text-slate-400 font-bold text-sm mb-8">Supported formats: PDF, TXT (Max 5MB)</p>
                    <input type="file" className="hidden" onChange={handleFileChange} accept=".pdf,.txt" />
                    <span className="py-4 px-10 bg-[#0f172a] dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black flex items-center gap-2">
                      Select File &rarr;
                    </span>
                  </label>
                ) : (
                  <div className="flex flex-col items-center">
                    <button 
                      onClick={startScan}
                      disabled={isScanning}
                      className="w-full max-w-sm py-4 bg-gradient-to-r from-sky-500 to-purple-600 text-white rounded-2xl font-black text-lg shadow-lg shadow-primary/20 flex items-center justify-center gap-3 transition-transform active:scale-95 disabled:opacity-70"
                    >
                      {isScanning ? (
                        <>
                          <div className="w-5 h-5 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Analyzing...
                        </>
                      ) : (
                        <>Get My Score &rarr;</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* RESULT STATE - image_bae6fa.png */
              <div className="animate-in fade-in zoom-in-95 duration-500 text-left border-t border-slate-100 dark:border-slate-800 pt-10">
                <div className="flex flex-col md:flex-row gap-12 items-center">
                  <div className="relative w-48 h-48 flex items-center justify-center shrink-0">
                    <svg className="w-full h-full -rotate-90">
                      <circle cx="96" cy="96" r="88" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-slate-100 dark:text-slate-800" />
                      <circle cx="96" cy="96" r="88" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={552} strokeDashoffset={552 - (552 * result.score) / 100} className="text-yellow-500 transition-all duration-1000" strokeLinecap="round" />
                    </svg>
                    <div className="absolute flex flex-col items-center">
                      <span className="text-6xl font-black text-slate-900 dark:text-white">{result.score}</span>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ATS Score</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-2">Analysis Report</h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium mb-8 leading-relaxed">
                      Your resume is well-structured but lacks quantifiable achievements in the experience section. (Demo Mode)
                    </p>
                    <div className="bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-800/50 rounded-2xl p-6 mb-8 text-left">
                      <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 font-black text-sm mb-4 uppercase tracking-wider">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                        Critical Improvements
                      </div>
                      <ul className="space-y-3">
                        {result.improvements.map((imp, i) => (
                          <li key={i} className="flex items-center gap-3 text-rose-500 dark:text-rose-300 font-bold text-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span> {imp}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-6 mb-10 border border-slate-100 dark:border-slate-700/50">
                      <div className="flex items-center gap-2 text-slate-900 dark:text-white font-black text-sm mb-4 uppercase tracking-wider">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/></svg>
                        Share Your Result
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-400 truncate font-medium">{result.link}</div>
                        <button className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-6 rounded-xl font-bold text-sm hover:opacity-80 transition flex items-center gap-2">Copy</button>
                      </div>
                    </div>
                    <button onClick={reset} className="py-4 px-8 border border-slate-200 dark:border-slate-700 rounded-2xl font-black text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition">Check Another</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left max-w-5xl mx-auto">
          {[
            { icon: '↺', title: 'What is ATS?', desc: 'Applicant Tracking Systems are robots that filter resumes before humans see them. 75% of resumes are rejected here.' },
            { icon: '📄', title: 'How we score', desc: 'We analyze keyword density, formatting parsability, and section structure against industry standards.' },
            { icon: '🛡️', title: 'Secure & Private', desc: 'Your resume is processed in memory and not stored. We prioritize your privacy.' }
          ].map((item, i) => (
            <div key={i} className="bg-white dark:bg-slate-900 p-8 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm transition-transform hover:-translate-y-1">
              <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-primary text-xl mb-6">{item.icon}</div>
              <h4 className="text-lg font-black text-slate-900 dark:text-white mb-3">{item.title}</h4>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default ATSChecker;